import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import {
  useTable,
  useSortBy,
  useExpanded,
  useFilters,
  useColumnOrder,
} from "react-table";
import styled from "styled-components/macro";
import {
  MdNavigateNext,
  MdNavigateBefore,
  MdExpandMore,
  MdExpandLess,
  MdPushPin,
  MdDragIndicator,
  MdFilterAlt,
  MdArrowUpward,
  MdArrowDownward,
} from "react-icons/md";
import ColumnMenu from "./ColumnMenu";
import TextColumnFilter from "./TextColumnFilter";
import columnFilterFn from "./helpers/columnFilterFn";
import smartSortType from "./helpers/smartSortType";
import columnTypeOf, { sortLabelsForType } from "./helpers/columnTypeOf";
import {
  operatorsForType,
  inputTypeForType,
} from "./helpers/filterOperators";

import {
  TableElement,
  Td,
  TdExpand,
  Th,
  ThSticky,
  Tr,
  TrSub,
  TableLoading,
  Thead,
  TBody,
  PaginationBar,
  RecordCount,
  PaginationControls,
  PaginationGroup,
  PageSizeSelect,
  JumpToPageInput,
  ArrowButton,
} from "./elements";
import { useCursorPagination } from "./plugins/useCursorPagination";
import SplashLoader from "../Loaders/SplashLoader";
import { StyledSelect, FormControl, Label } from "../Form/FormControls";

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// Sort indicator shared by header cells.
const SortIndicator = ({ column }) => (
  <span>
    {column.isSorted ? column.isSortedDesc ? <MdExpandMore /> : <MdExpandLess /> : ""}
  </span>
);

// --- Opt-in column menu header (AC-15377) --------------------------------
// Rendered only for columns with `enableColumnMenu`. Left drag handle (visual
// only — no reorder), label, active sort arrow (↑/↓), solid-blue funnel when a
// filter is active, and the always-visible chevron that opens <ColumnMenu>.
// Columns WITHOUT enableColumnMenu keep the legacy header exactly, so the other
// ~37 DataTable consumers are untouched (the AC-15498 opt-in constraint).
//
// LIMITATION: the column menu is wired only into the non-pinning header branch.
// A consumer using both `pinning` and `enableColumnMenu` gets the legacy pinned
// header with no menu. Not needed for Policies; revisit if a pinned table opts
// into the menu.
const MenuHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

// Drag handle for column reordering (opt-in via DataTable `reorderable`). The
// handle is the drag source; the header cell (Th) is the drop target. Shown on
// all reorderable columns — both menu and legacy headers.
const DragHandle = ({ dragHandleProps }) => (
  <span
    {...dragHandleProps}
    draggable
    title="Drag to reorder column"
    style={{
      display: "inline-flex",
      alignItems: "center",
      cursor: "grab",
      opacity: 0.4,
      flexShrink: 0,
    }}
  >
    <MdDragIndicator />
  </span>
);

const ColumnMenuHeader = ({
  column,
  reorderable,
  dragHandleProps,
  sortLabels,
  filterOperators,
  filterInputType,
}) => (
  <MenuHeaderContainer>
    {reorderable ? <DragHandle dragHandleProps={dragHandleProps} /> : null}
    <span>{column.render("Header")}</span>
    {column.isSorted ? (
      column.isSortedDesc ? (
        <MdArrowDownward style={{ flexShrink: 0 }} />
      ) : (
        <MdArrowUpward style={{ flexShrink: 0 }} />
      )
    ) : null}
    {column.filterValue ? (
      <MdFilterAlt style={{ color: "#2563eb", flexShrink: 0 }} />
    ) : null}
    <ColumnMenu column={column} sortLabels={column.sortLabels ?? sortLabels}>
      {typeof column.renderFilterMenu === "function"
        ? column.renderFilterMenu(column)
        : column.filterable === false
        ? null
        : (
          <TextColumnFilter
            column={column}
            operators={filterOperators}
            inputType={filterInputType}
          />
        )}
    </ColumnMenu>
  </MenuHeaderContainer>
);

// --- Optional left-pinned (sticky) column support -------------------------
// Used only when `pinning` is set. Columns are matched by their (string)
// Header label, mirroring the legacy PagedTable pinning behavior so migrated
// consumers (e.g. FailureContextTable) keep sticky columns + the quick-pin UI.

const PinnableHeaderCell = ({ headerGroup, sticky, setSticky, header, pinning }) => {
  const column = headerGroup.headers.find((h) => h.Header === header);
  if (!column) return null;
  const isSticky = sticky.find((s) => s.label === column?.Header);
  const existingIndex = sticky.findIndex((s) => s.label === column?.Header);
  const isActions = column?.id === "actions";
  const ColumnSelector = isSticky || isActions ? ThSticky : Th;

  return (
    <ColumnSelector
      {...column?.getHeaderProps(column?.getSortByToggleProps())}
      left={isSticky ? `${existingIndex * 180}px` : null}
      isLast={isSticky ? existingIndex + 1 === sticky.length : null}
      right={isActions ? `0` : null}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        {column?.render("Header")}
        <SortIndicator column={column} />
        {pinning && column?.Header && column?.Header?.trim?.() !== "" && (
          <div
            style={{
              marginLeft: "auto",
              paddingLeft: "1rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}
          >
            <MdPushPin
              style={{ opacity: isSticky ? 1 : 0.4 }}
              onClick={() =>
                setSticky((prev) => {
                  const clone = [...prev];
                  if (existingIndex > -1) {
                    clone.splice(existingIndex, 1);
                  } else if (!clone.some((item) => item?.label === column?.Header)) {
                    clone.push({ label: column?.Header, value: column?.id });
                  }
                  return clone;
                })
              }
            />
          </div>
        )}
      </div>
    </ColumnSelector>
  );
};

const PinnableCell = ({ row, sticky, header, i }) => {
  const cell = row.cells.find((c) => c.column.Header === header);
  if (!cell) return null;
  const isSticky = sticky.find((s) => s.label === cell?.column?.Header);
  const existingIndex = sticky.findIndex((s) => s.label === cell?.column?.Header);
  const isActions = cell?.column?.id === "actions";
  return (
    <Td
      {...cell?.getCellProps()}
      isLast={isSticky ? existingIndex + 1 === sticky.length : null}
      left={isSticky ? `${existingIndex * 180}px` : null}
      isOdd={i % 2 !== 1}
      right={isActions ? `0` : null}
    >
      {cell?.render("Cell")}
    </Td>
  );
};

// Single shared, reusable table + pagination component (AC-15379).
//
// Modes (set via `mode`, default "server"):
//   - "server"     — `data` is a relay connection's `edges`. REQUIRES
//                     `fetchData` and a numeric `totalCount`: the offset UX
//                     (range, page count, jump-to-page) is derived from it.
//                     Without `totalCount` the table silently collapses to a
//                     single page driven only by `pageInfo.hasNextPage`.
//                     `pageInfo` is optional (used only as a next/prev fallback).
//   - "clientSide" — `data` is a flat row array loaded in full; the component
//                     sorts/paginates in the browser. For small/bounded tables.
//
// IMPORTANT (server mode): `fetchData` MUST be referentially stable — memoize it
// with `useCallback`. It is a dependency of the fetch effect, which is also how
// callers trigger a refetch when their filter/where inputs change (rebuild the
// memoized callback). An inline/unmemoized function would refetch every render.
//
// The standardized bar (FR-1..FR-7) is identical in both modes:
//   "X–Y of Z"  |  Records per page [dropdown]  |  Page [N] of M  |  < >
const DataTable = ({
  columns,
  data = [],
  loading,
  mode = "server",
  fetchData,
  totalCount,
  pageInfo,
  // Fallback only. The legacy components this replaced had different defaults
  // (SortTable 50, PagedTable/PagedTableRedux 10), so relying on this value
  // silently changed a table's rows-per-page on migration (AC-15843). Every
  // paginated call site now pins its own legacy value explicitly; keep doing
  // that rather than leaning on this default.
  defaultPageSize = 25,
  defaultSort,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  maxSize,
  renderRowSubComponent,
  getRowProps = () => ({}),
  noMargin,
  noResultsText = "No Results",
  onPageSizeChange,
  disableSortBy = false,
  pinning = false,
  pinned,
  // Opaque-cursor server mode for the bespoke Cursor.cs endpoints (e.g.
  // retrieveFailureContext). When true, the plugin threads the backend-supplied
  // `endCursor` forward for next-page nav and keeps a previousCursors stack for
  // back-nav, instead of constructing a positional cursor. The client treats
  // the backend cursor as opaque — it can't synthesize the cursor for an
  // arbitrary page — so jump-to-page is unsupported and the input is replaced
  // by a static page indicator (matching the legacy PagedTable UX, which also
  // only exposed prev/next here). Consumers MUST pass the current page's
  // `endCursor`.
  keysetCursor = false,
  endCursor,
  // SortTable parity (clientSide): hide the bar + show all rows; don't reset
  // page on data change; relax the scroll container.
  hidePagination = false,
  noScroll = false,
  // Height cap for the scrolling body. Defaults to the 70vh every table has
  // used; consumers embedded inside a form or modal (where a 70vh grid pushes
  // the form's own buttons off screen) pass something shorter.
  maxHeight = "70vh",
  removeOverflow = false,
  dontReset = false,
  // Opt-in column reordering (AC-15377). When true, reorderable columns get a
  // drag handle and can be dragged to reorder (HTML5 DnD + useColumnOrder).
  // Order is session-only (resets on remount). Off for every other consumer.
  reorderable = false,
  // Table-level column-menu opt-in (AC-15498). When true, every eligible data
  // column (non-blank string Header, not the actions column) gets the column
  // menu automatically; opt a column out with `disableColumnMenu: true`. Only
  // the in-scope tables set this, so other DataTable consumers are unchanged.
  // Per-column `enableColumnMenu: true` still works independently (Policies).
  columnMenu = false,
}) => {
  const isServer = mode !== "clientSide";
  const isExpandable = typeof renderRowSubComponent === "function";
  const [sticky, setSticky] = useState(pinned ?? []);

  // When the bar is hidden, render every row on one page (matches SortTable's
  // hidePagination behavior). A large page size keeps all rows in the slice
  // regardless of when async data arrives.
  const initialPageSize = hidePagination ? Number.MAX_SAFE_INTEGER : defaultPageSize;

  // Plugin order follows react-table's canonical sequence: useColumnOrder,
  // useFilters, useSortBy, (useExpanded), then the cursor pagination plugin
  // (whose ensurePluginOrder expects filters/sort before it). useFilters powers
  // the opt-in column menu's filter state and — via the cursor reducer's
  // setFilter handling — the page-1 reset on filter change (FR-11).
  const tablePlugins = [useColumnOrder, useFilters, useSortBy];
  if (isExpandable) tablePlugins.push(useExpanded);
  tablePlugins.push(useCursorPagination);

  // Default the column filter to the {operator, values}-aware client filter so
  // clientSide tables filter in the browser. Inert in server mode and for
  // columns with no active filter, so other consumers are unaffected.
  // `sortType` orders numbers as numbers and dates as dates, falling back to
  // react-table's own alphanumeric for everything else, so the menu's
  // "Smallest to Largest" / "Oldest to Newest" wording matches the actual
  // ordering (AC-15842). Inert in server mode (manualSortBy).
  const defaultColumn = React.useMemo(
    () => ({ filter: columnFilterFn, sortType: smartSortType }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    rows,
    preFilteredRows,
    visibleColumns,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setColumnOrder,
    state: { pageIndex, pageSize, cursor, sortBy, filters },
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      disableSortRemove: true,
      // Consumers that drive their own (e.g. server-side) sort UI pass
      // disableSortBy so the built-in header sort doesn't double-handle clicks.
      disableSortBy,
      autoResetExpanded: false,
      // In server mode a page fetch changes `data`; don't let react-table's
      // sort/page auto-reset fire on it (it would wipe the user's sort or loop).
      // Sort changes still reset to page 1 via the cursor plugin's reducer.
      autoResetSortBy: !isServer,
      autoResetPage: !dontReset,
      // Server mode owns filtering: setFilter only updates state.filters (which
      // is surfaced to fetchData); don't filter rows client-side, and don't
      // wipe filters on each refetch. Filter changes reset to page 1 via the
      // cursor plugin's reducer. Client tables that opt out of page reset
      // (dontReset) do so because their `data` reference changes on refresh;
      // honor that here too so an applied column filter survives the refresh.
      autoResetFilters: !isServer && !dontReset,
      initialState: {
        pageIndex: 0,
        pageSize: initialPageSize,
        cursor: null,
        sortBy: defaultSort ?? [],
        filters: [],
      },
      manualPagination: isServer,
      manualSortBy: isServer,
      manualFilters: isServer,
      totalCount,
      pageInfo,
      keysetCursor: isServer && keysetCursor,
      endCursor,
    },
    ...tablePlugins
  );

  // What each column holds — numeric, date or text. Drives both the sort-direction
  // wording (AC-15842) and the filter operator set / value input, so the two can't
  // disagree. Resolved from the instance-level `preFilteredRows` rather than a
  // column's own: react-table drops the per-column collection once that column has
  // an active filter, which made the labels flip mid-use.
  const inferredColumnTypes = React.useMemo(() => {
    const byId = {};
    visibleColumns.forEach((c) => {
      byId[c.id] = columnTypeOf(preFilteredRows, c.id);
    });
    return byId;
  }, [visibleColumns, preFilteredRows]);

  // Server mode owns the GraphQL fetch; refetch whenever page/size/sort change.
  // `fetchData` stays in the deps by design (see header note).
  useEffect(() => {
    if (isServer && typeof fetchData === "function") {
      fetchData({ pageSize, cursor, sortBy, filters });
    }
  }, [isServer, fetchData, pageIndex, pageSize, cursor, sortBy, filters]);

  // --- Column reordering (opt-in) ------------------------------------------
  // HTML5 drag-and-drop: the drag handle is the source, the header cell the
  // drop target. The actions column is excluded and always kept last.
  const draggedColRef = useRef(null);

  const dragSourceProps = (id) => ({
    onDragStart: (e) => {
      draggedColRef.current = id;
      e.dataTransfer.effectAllowed = "move";
      try {
        e.dataTransfer.setData("text/plain", id);
      } catch (_) {
        // Some browsers restrict setData; the ref is the source of truth.
      }
    },
    onDragEnd: () => {
      draggedColRef.current = null;
    },
  });

  const dropTargetProps = (targetId) => ({
    onDragOver: (e) => {
      if (draggedColRef.current && draggedColRef.current !== targetId) {
        e.preventDefault();
      }
    },
    onDrop: (e) => {
      e.preventDefault();
      const fromId = draggedColRef.current;
      draggedColRef.current = null;
      if (!fromId || fromId === targetId) return;
      const order = visibleColumns.map((c) => c.id);
      const from = order.indexOf(fromId);
      const to = order.indexOf(targetId);
      if (from < 0 || to < 0) return;
      order.splice(to, 0, order.splice(from, 1)[0]);
      // Keep the sticky actions column pinned to the end.
      const actionsIdx = order.indexOf("actions");
      if (actionsIdx > -1) {
        order.splice(actionsIdx, 1);
        order.push("actions");
      }
      setColumnOrder(order);
    },
  });

  // Surface the silent single-page collapse when a server table omits totalCount.
  useEffect(() => {
    if (
      process.env.NODE_ENV !== "production" &&
      isServer &&
      typeof totalCount !== "number"
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        '<DataTable mode="server"> requires a numeric `totalCount` for the ' +
          "offset UX (range, page count, jump-to-page); without it the table " +
          "collapses to a single page."
      );
    }
  }, [isServer, totalCount]);

  // --- Pagination bar derived values ---------------------------------------
  // Client mode counts the rows that survived filtering, not the whole dataset —
  // using data.length left the total at its unfiltered value, so a filter that
  // narrowed 1120 rows to 14 still read "1–14 of 1120" (AC-15897). `rows` is
  // post-filter and pre-pagination, which is exactly the denominator the range
  // is expressed against. Server mode keeps totalCount: the server already
  // applies the filter before counting.
  const resolvedTotal = isServer ? totalCount : rows.length;
  const totalPages = pageCount > 0 ? pageCount : 1;
  const rangeStart = page.length ? pageIndex * pageSize + 1 : 0;
  const rangeEnd = pageIndex * pageSize + page.length;
  const recordLabel =
    typeof resolvedTotal === "number"
      ? `${rangeStart}–${rangeEnd} of ${resolvedTotal}`
      : `${rangeStart}–${rangeEnd}`;

  // --- Jump-to-page input ---------------------------------------------------
  const [pageInputValue, setPageInputValue] = useState(String(pageIndex + 1));
  const [shake, setShake] = useState(false);

  // Keep the input in sync with the current page after any navigation
  // (arrows, per-page change, or the input itself).
  useEffect(() => {
    setPageInputValue(String(pageIndex + 1));
  }, [pageIndex]);

  const commitJump = () => {
    const raw = pageInputValue.trim();
    if (raw === "") {
      // Empty + Enter is a no-op (FR-5).
      return;
    }

    const parsed = Number(raw);
    const maxIndex = totalPages - 1;

    if (!Number.isInteger(parsed)) {
      // Defensive: with type="number" the browser usually coerces invalid
      // input to "" first, but under direct value-set (tests) shake + snap back.
      setShake(true);
      setPageInputValue(String(pageIndex + 1));
      return;
    }

    const targetIndex = parsed - 1;
    if (targetIndex < 0 || targetIndex > maxIndex) {
      // Out of range: shake and snap to the nearest valid page (FR-5).
      const snapped = targetIndex < 0 ? 0 : maxIndex;
      setShake(true);
      setPageInputValue(String(snapped + 1));
      if (snapped !== pageIndex) gotoPage(snapped);
      return;
    }

    if (targetIndex !== pageIndex) {
      gotoPage(targetIndex);
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    if (typeof onPageSizeChange === "function") {
      onPageSizeChange(newSize);
    }
  };

  // `maxSize` caps the per-page dropdown to a single option (legacy PagedTable
  // behavior) for tables whose backend query enforces a hard page-size limit
  // (e.g. FailureContextTable, backend max 50). It prevents offering a size the
  // [UsePaging] connection would reject.
  // Otherwise the dropdown must offer `defaultPageSize` itself: the select is
  // driven by `value={pageSize}`, so a default that isn't one of the options
  // (e.g. the 5-row grid embedded in the upload form) leaves the select with no
  // match and the browser displays the first option instead — the grid pages by
  // 5 while the dropdown reads 10.
  const sizeOptions =
    typeof maxSize === "number"
      ? [maxSize]
      : pageSizeOptions.includes(defaultPageSize)
        ? pageSizeOptions
        : [...pageSizeOptions, defaultPageSize].sort((a, b) => a - b);

  // --- Anchor the pagination bar -------------------------------------------
  // On multi-page tables the last page is shorter than a full page, so the
  // content region shrinks and the bar jumps up between pages. Reserve the
  // tallest content height seen for the current page size (offsetHeight is
  // already clamped to the scroll cap) so short pages keep the bar in a
  // fixed position. Only engages when more than one page exists — single-page
  // tables can't bounce, so they reserve nothing and avoid empty whitespace.
  const scrollRef = useRef(null);
  const [reservedHeight, setReservedHeight] = useState(0);
  const anchorBar = !hidePagination && !noScroll && pageCount > 1;

  // Rows-per-page changes the full-page baseline — restart the reservation.
  useEffect(() => {
    setReservedHeight(0);
  }, [pageSize]);

  useLayoutEffect(() => {
    if (!anchorBar) return;
    const measured = scrollRef.current?.offsetHeight ?? 0;
    // Monotonic max → stabilises at the full-page height, never loops.
    setReservedHeight((prev) => (measured > prev ? measured : prev));
  }, [anchorBar, page, pageSize]);

  return (
    <div>
      {pinning ? (
        <FormControl>
          <Label>Quick Column Pin</Label>
          <StyledSelect
            name="qpn"
            id="qpn"
            inputId="qpn-input"
            instanceId="qpn-instance"
            options={columns
              ?.filter(
                (c) => typeof c?.Header === "string" && c.Header.trim() !== ""
              )
              .map((c) => ({ label: c?.Header, value: c?.id }))}
            placeholder="Select Columns to Pin Left"
            value={sticky ?? []}
            menuPlacement="auto"
            isMulti
            onChange={(e) => setSticky(e || [])}
            menuPortalTarget={document.body}
          />
        </FormControl>
      ) : null}

      <div
        ref={scrollRef}
        style={{
          position: "relative",
          maxHeight: noScroll ? "none" : maxHeight,
          minHeight: anchorBar && reservedHeight ? reservedHeight : undefined,
          overflow: removeOverflow ? "inherit" : "auto",
        }}
      >
        <TableElement noMargin={noMargin} className="tableFixHead" {...getTableProps()}>
          <Thead className="thead">
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {pinning
                  ? [
                      ...sticky.map((s) => (
                        <PinnableHeaderCell
                          key={`pin-h-${s?.label}`}
                          headerGroup={headerGroup}
                          sticky={sticky}
                          setSticky={setSticky}
                          header={s?.label}
                          pinning={pinning}
                        />
                      )),
                      ...headerGroup.headers
                        .filter((h) => !sticky.some((s) => s.label === h?.Header))
                        .map((column) => (
                          <PinnableHeaderCell
                            key={`h-${column.id}`}
                            headerGroup={headerGroup}
                            sticky={sticky}
                            setSticky={setSticky}
                            header={column?.Header}
                            pinning={pinning}
                          />
                        )),
                    ]
                  : headerGroup.headers.map((column) => {
                      const isActions = column?.id === "actions";
                      const ColumnSelector = isActions ? ThSticky : Th;
                      // Columns that shrink to their content (actions, or any
                      // column opting in via `shrink`) instead of absorbing
                      // leftover table width.
                      const shrink = isActions || column.shrink === true;
                      // The actions column stays pinned; everything else is
                      // reorderable when the table opts in, unless the column
                      // opts out via `disableReorder`.
                      const canReorder =
                        reorderable && !isActions && column.disableReorder !== true;
                      const dropProps = canReorder
                        ? dropTargetProps(column.id)
                        : {};
                      const dragProps = canReorder
                        ? dragSourceProps(column.id)
                        : null;
                      // Show the column menu when either the column opts in
                      // explicitly (enableColumnMenu, e.g. Policies) or the
                      // table opts in (columnMenu) and this column is an
                      // eligible data column not opted out (AC-15498).
                      const isEligibleForMenu =
                        typeof column.Header === "string" &&
                        column.Header.trim() !== "" &&
                        !isActions;
                      const showMenu =
                        column.enableColumnMenu === true ||
                        (columnMenu &&
                          column.disableColumnMenu !== true &&
                          isEligibleForMenu);
                      // Opt-in column menu: render the AC-15377 header (sort
                      // lives in the menu, so DON'T spread getSortByToggleProps
                      // here — header clicks shouldn't sort).
                      if (showMenu) {
                        return (
                          <ColumnSelector
                            key={column.id}
                            {...column.getHeaderProps()}
                            {...dropProps}
                            right={isActions ? `0` : null}
                          >
                            <ColumnMenuHeader
                              column={column}
                              reorderable={canReorder}
                              dragHandleProps={dragProps}
                              sortLabels={sortLabelsForType(
                                inferredColumnTypes[column.id]
                              )}
                              // Typed operators are client-side only: server
                              // consumers translate filters via
                              // textFilterToWhere, which handles the string
                              // operators only, so a numeric/date comparison
                              // would be silently dropped. Server tables keep
                              // the text set until that wiring exists.
                              filterOperators={
                                isServer
                                  ? undefined
                                  : operatorsForType(
                                      inferredColumnTypes[column.id]
                                    )
                              }
                              filterInputType={
                                isServer
                                  ? "text"
                                  : inputTypeForType(
                                      inferredColumnTypes[column.id]
                                    )
                              }
                            />
                          </ColumnSelector>
                        );
                      }
                      return (
                        <ColumnSelector
                          key={column.id}
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          {...dropProps}
                          right={isActions ? `0` : null}
                          // Shrink-to-content columns (actions, pin) get a
                          // content width instead of absorbing leftover table
                          // width (width:1% + nowrap is the standard trick).
                          style={
                            shrink
                              ? { width: "1%", whiteSpace: "nowrap" }
                              : undefined
                          }
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              // Right-aligned columns (e.g. numeric) push the
                              // label to the right edge to match the data.
                              justifyContent:
                                column.align === "right"
                                  ? "flex-end"
                                  : undefined,
                            }}
                          >
                            {canReorder ? (
                              <DragHandle dragHandleProps={dragProps} />
                            ) : null}
                            <div
                              style={{
                                marginRight:
                                  column.align === "right" ? undefined : "auto",
                              }}
                            >
                              {column.render("Header")}
                            </div>
                            <SortIndicator column={column} />
                          </div>
                        </ColumnSelector>
                      );
                    })}
              </Tr>
            ))}
          </Thead>
          <TBody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <React.Fragment key={row.id}>
                  <Tr {...row.getRowProps(getRowProps(row))}>
                    {pinning
                      ? [
                          ...sticky.map((s) => (
                            <PinnableCell
                              key={`pin-c-${s?.label}`}
                              row={row}
                              sticky={sticky}
                              header={s?.label}
                              i={i}
                            />
                          )),
                          ...row.cells
                            .filter(
                              (c) => !sticky.some((s) => s.label === c.column?.Header)
                            )
                            .map((cell) => (
                              <PinnableCell
                                key={`c-${cell.column.id}`}
                                row={row}
                                sticky={sticky}
                                header={cell?.column?.Header}
                                i={i}
                              />
                            )),
                        ]
                      : row.cells.map((cell) => {
                          const isActions = cell.column?.id === "actions";
                          const shrink =
                            isActions || cell.column?.shrink === true;
                          return (
                            <Td
                              key={cell.column.id}
                              {...cell.getCellProps()}
                              right={isActions ? `0` : null}
                              isOdd={i % 2 !== 1}
                              // Shrink-to-content cells (actions, pin) match
                              // their header width instead of absorbing slack.
                              style={
                                shrink
                                  ? { width: "1%", whiteSpace: "nowrap" }
                                  : undefined
                              }
                            >
                              {cell.render("Cell")}
                            </Td>
                          );
                        })}
                  </Tr>
                  {isExpandable && row.isExpanded ? (
                    <TrSub>
                      <TdExpand colSpan={visibleColumns.length}>
                        {renderRowSubComponent({ row })}
                      </TdExpand>
                    </TrSub>
                  ) : null}
                </React.Fragment>
              );
            })}
          </TBody>
        </TableElement>
        {loading ? (
          <TableLoading>
            <SplashLoader text="Loading Results" />
          </TableLoading>
        ) : null}
      </div>

      {!loading && page && !page.length ? (
        <div style={{ padding: "0.75rem 0" }}>{noResultsText}</div>
      ) : null}

      {hidePagination ? null : (
      <PaginationBar>
        <RecordCount aria-label="Record range">{recordLabel}</RecordCount>

        <PaginationControls>
          <PaginationGroup>
            <span>Records per page:</span>
            <PageSizeSelect
              aria-label="Records per page"
              value={pageSize}
              onChange={handlePageSizeChange}
            >
              {sizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </PageSizeSelect>
          </PaginationGroup>

          <PaginationGroup>
            <span>Page</span>
            {keysetCursor ? (
              // Keyset mode: jump-to-page can't be supported (the backend only
              // resumes from the last row returned), so show a read-only page
              // position instead of the editable input.
              <span aria-label="Current page">{pageIndex + 1}</span>
            ) : (
              <JumpToPageInput
                type="number"
                aria-label="Jump to page"
                data-shake={shake ? "true" : "false"}
                value={pageInputValue}
                min={1}
                max={totalPages}
                onChange={(e) => setPageInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    // Several tables render inside a <form> (workgroup forms,
                    // workspace widget config). Without this, Enter submits the
                    // form and the browser fires native validation on unrelated
                    // required fields — "Please fill out this field" (AC-15838).
                    e.preventDefault();
                    commitJump();
                  }
                }}
                onBlur={commitJump}
                onAnimationEnd={() => setShake(false)}
              />
            )}
            <span>of {totalPages}</span>
          </PaginationGroup>

          <PaginationGroup>
            <ArrowButton
              type="button"
              aria-label="Previous page"
              onClick={() => previousPage()}
              disabled={!canPreviousPage || loading}
            >
              <MdNavigateBefore />
            </ArrowButton>
            <ArrowButton
              type="button"
              aria-label="Next page"
              onClick={() => nextPage()}
              disabled={!canNextPage || loading}
            >
              <MdNavigateNext />
            </ArrowButton>
          </PaginationGroup>
        </PaginationControls>
      </PaginationBar>
      )}
    </div>
  );
};

export default DataTable;
